/*
 * Archive Rewards: minimum eligible order for the Active Reward Voucher tile.
 *
 * Reward Vouchers cover up to 5% of the eligible order value. Until that can be applied at
 * checkout, a points-funded Reward Voucher's Shopify discount is only accepted on eligible items
 * worth at least 100 / 5 = 20x its value, and this module shows that minimum on the tile.
 *
 * Only points-funded Reward Vouchers use the minimum. The tile's voucher is the profile's active
 * Reward Voucher, and every Reward Voucher code carries the backend's "ARCHIVE-" prefix
 * (ArchiveRewardVoucherCode.GetPrefix(Reward)); gift vouchers use BDAY-, ANNIV-, PROMO-, MILE-,
 * GOOD- and OFFER-. Anything else shows no minimum.
 *
 * Pure helpers and the popover state machine have no DOM access, so they are unit tested in Node.
 */
(function (root) {
  "use strict";

  const MAXIMUM_REDEMPTION_PERCENT = 5;
  const REWARD_VOUCHER_PREFIX = "ARCHIVE-";
  const HOVER_CLOSE_DELAY_MS = 180;
  const VIEWPORT_MARGIN = 12;
  const OFFSET = 10;

  const wholeDollars = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  const dollarsAndCents = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  function appliesTo(voucherCode, amountCents) {
    const amount = Number(amountCents);

    return typeof voucherCode === "string" &&
      voucherCode.trim().toUpperCase().startsWith(REWARD_VOUCHER_PREFIX) &&
      Number.isInteger(amount) &&
      amount > 0;
  }

  function minimumEligibleOrderCents(amountCents) {
    return Number(amountCents) * 100 / MAXIMUM_REDEMPTION_PERCENT;
  }

  // "$200" for whole dollars, "$200.50" otherwise.
  function formatMoney(cents) {
    const value = Number(cents) / 100;

    return Number.isInteger(value) ? wholeDollars.format(value) : dollarsAndCents.format(value);
  }

  function describe(voucherCode, amountCents) {
    if (!appliesTo(voucherCode, amountCents)) {
      return null;
    }

    const voucher = formatMoney(amountCents);
    const minimum = formatMoney(minimumEligibleOrderCents(amountCents));

    return {
      minimumCents: minimumEligibleOrderCents(amountCents),
      minimumText: minimum,
      label: `Minimum eligible order ${minimum}`,
      buttonLabel: `About the ${minimum} minimum eligible order for this voucher`,
      message: `Reward Vouchers can cover up to ${MAXIMUM_REDEMPTION_PERCENT}% of your eligible order value. ` +
        `This ${voucher} voucher requires at least ${minimum} in eligible items.`,
      note: "Shipping does not count toward the minimum."
    };
  }

  /*
   * Popover state: hover opens it transiently (closing shortly after the pointer leaves both the
   * trigger and the popover, so moving between them never flickers); a click/tap or keyboard
   * activation pins it open until X, an outside click/tap or Escape. `schedule`/`cancel` are
   * injected timers so the behaviour is testable without a browser.
   */
  function createPopoverState({ onChange, schedule, cancel }) {
    let open = false;
    let pinned = false;
    let pointerInside = false;
    let closeTimer = null;

    function set(nextOpen, nextPinned, reason) {
      const changed = nextOpen !== open || nextPinned !== pinned;
      open = nextOpen;
      pinned = nextOpen && nextPinned;

      if (changed) {
        onChange({ open, pinned, reason });
      }
    }

    function cancelClose() {
      if (closeTimer !== null) {
        cancel(closeTimer);
        closeTimer = null;
      }
    }

    return {
      get open() { return open; },
      get pinned() { return pinned; },

      pointerEnter() {
        pointerInside = true;
        cancelClose();

        if (!open) {
          set(true, false, "hover");
        }
      },

      pointerLeave() {
        pointerInside = false;

        if (!open || pinned) {
          return;
        }

        cancelClose();
        closeTimer = schedule(() => {
          closeTimer = null;

          if (!pointerInside && !pinned) {
            set(false, false, "hover-out");
          }
        }, HOVER_CLOSE_DELAY_MS);
      },

      // Click, tap, Enter or Space on the info button.
      activate() {
        cancelClose();

        if (open && pinned) {
          set(false, false, "toggle");
          return;
        }

        set(true, true, "activate");
      },

      close(reason = "close") {
        cancelClose();
        set(false, false, reason);
      }
    };
  }

  // Fixed-position placement in viewport coordinates. On wider screens it opens beside the whole
  // voucher card (`card`, the tile at the right edge), level with the icon, so it never covers the
  // voucher's own code or Convert/Copy buttons; otherwise below the icon, or above it near the
  // bottom of the screen. Always clamped inside the viewport.
  function place(anchor, popover, viewport, card = anchor) {
    const width = Math.min(popover.width, viewport.width - VIEWPORT_MARGIN * 2);
    const clampLeft = left => Math.min(Math.max(left, VIEWPORT_MARGIN), viewport.width - VIEWPORT_MARGIN - width);
    const clampTop = top => Math.min(Math.max(top, VIEWPORT_MARGIN), Math.max(VIEWPORT_MARGIN, viewport.height - VIEWPORT_MARGIN - popover.height));

    if (card.left - OFFSET - width >= VIEWPORT_MARGIN && viewport.width > 720) {
      return {
        side: "left",
        width,
        left: card.left - OFFSET - width,
        top: clampTop(anchor.top + anchor.height / 2 - popover.height / 2)
      };
    }

    const below = anchor.bottom + OFFSET;
    const fitsBelow = below + popover.height <= viewport.height - VIEWPORT_MARGIN;
    const above = anchor.top - OFFSET - popover.height;

    return {
      side: fitsBelow || above < VIEWPORT_MARGIN ? "below" : "above",
      width,
      left: clampLeft(anchor.left + anchor.width / 2 - width / 2),
      top: fitsBelow || above < VIEWPORT_MARGIN ? clampTop(below) : above
    };
  }

  // ---- DOM controller ------------------------------------------------------------------

  let popoverElement = null;
  let controller = null;
  let currentTrigger = null;

  function ensurePopover(doc) {
    if (popoverElement && popoverElement.isConnected) {
      return popoverElement;
    }

    popoverElement = doc.createElement("div");
    popoverElement.id = "archiveRewardsVoucherMinimumPopover";
    popoverElement.className = "archive-rewards-minimum-popover";
    popoverElement.setAttribute("role", "dialog");
    popoverElement.setAttribute("aria-modal", "false");
    popoverElement.setAttribute("aria-labelledby", "archiveRewardsVoucherMinimumTitle");
    popoverElement.hidden = true;
    popoverElement.innerHTML = `
      <button type="button" class="archive-rewards-minimum-popover__close" aria-label="Close" data-minimum-popover-close>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12"/><path d="M18 6L6 18"/></svg>
      </button>
      <p class="archive-rewards-minimum-popover__title" id="archiveRewardsVoucherMinimumTitle"></p>
      <p class="archive-rewards-minimum-popover__message" data-minimum-popover-message></p>
      <p class="archive-rewards-minimum-popover__note" data-minimum-popover-note></p>
    `;
    doc.body.appendChild(popoverElement);

    return popoverElement;
  }

  function init(doc) {
    const win = doc.defaultView;
    const row = doc.querySelector("[data-rewards-voucher-minimum]");
    const trigger = row ? row.querySelector("[data-rewards-voucher-minimum-info]") : null;

    if (!row || !trigger || controller) {
      return;
    }

    currentTrigger = trigger;
    const popover = ensurePopover(doc);
    trigger.setAttribute("aria-controls", popover.id);
    trigger.setAttribute("aria-expanded", "false");

    function position() {
      if (popover.hidden) {
        return;
      }

      const rect = trigger.getBoundingClientRect();
      const card = (trigger.closest(".archive-rewards-card") || trigger).getBoundingClientRect();
      popover.style.width = "";
      const box = popover.getBoundingClientRect();
      const placement = place(
        { left: rect.left, top: rect.top, width: rect.width, height: rect.height, bottom: rect.bottom },
        { width: Math.min(box.width || 288, 288), height: box.height },
        { width: doc.documentElement.clientWidth, height: win.innerHeight },
        { left: card.left });

      popover.style.width = `${placement.width}px`;
      popover.style.left = `${Math.round(placement.left)}px`;
      popover.style.top = `${Math.round(placement.top)}px`;
      popover.dataset.side = placement.side;
    }

    let frame = 0;
    function reposition() {
      if (!frame) {
        frame = win.requestAnimationFrame(() => {
          frame = 0;
          position();
        });
      }
    }

    controller = createPopoverState({
      schedule: (callback, delay) => win.setTimeout(callback, delay),
      cancel: id => win.clearTimeout(id),
      onChange: ({ open, reason }) => {
        popover.hidden = !open;
        trigger.setAttribute("aria-expanded", String(open));
        row.classList.toggle("is-open", open);

        if (open) {
          position();
          win.addEventListener("scroll", reposition, { passive: true, capture: true });
          win.addEventListener("resize", reposition);
        } else {
          win.removeEventListener("scroll", reposition, { capture: true });
          win.removeEventListener("resize", reposition);

          // Keyboard users closing from inside the popover return to the info button.
          if (popover.contains(doc.activeElement) && reason !== "outside") {
            trigger.focus({ preventScroll: true });
          }
        }
      }
    });

    const onPointerEnter = event => {
      if (event.pointerType === "mouse") {
        controller.pointerEnter();
      }
    };
    const onPointerLeave = event => {
      if (event.pointerType === "mouse") {
        controller.pointerLeave();
      }
    };

    trigger.addEventListener("pointerenter", onPointerEnter);
    trigger.addEventListener("pointerleave", onPointerLeave);
    popover.addEventListener("pointerenter", onPointerEnter);
    popover.addEventListener("pointerleave", onPointerLeave);

    trigger.addEventListener("click", event => {
      event.preventDefault();
      controller.activate();

      // Opened from the keyboard (Enter/Space report detail 0): move focus into the popover.
      if (controller.open && event.detail === 0) {
        popover.querySelector("[data-minimum-popover-close]").focus({ preventScroll: true });
      }
    });

    popover.querySelector("[data-minimum-popover-close]").addEventListener("click", () => controller.close("close-button"));

    doc.addEventListener("pointerdown", event => {
      if (controller.open && !popover.contains(event.target) && !trigger.contains(event.target)) {
        controller.close("outside");
      }
    }, true);

    doc.addEventListener("keydown", event => {
      if (event.key === "Escape" && controller.open) {
        event.preventDefault();
        controller.close("escape");
      }
    });
  }

  // Called by archive-rewards.js whenever the tile's voucher changes (or disappears).
  function render(doc, voucherCode, amountCents) {
    const row = doc.querySelector("[data-rewards-voucher-minimum]");

    if (!row) {
      return;
    }

    init(doc);

    if (controller) {
      controller.close("render");
    }

    const details = describe(voucherCode, amountCents);
    row.hidden = !details;

    if (!details) {
      return;
    }

    row.querySelector("[data-rewards-voucher-minimum-amount]").textContent = details.minimumText;
    currentTrigger.setAttribute("aria-label", details.buttonLabel);

    const popover = ensurePopover(doc);
    popover.querySelector("#archiveRewardsVoucherMinimumTitle").textContent = details.label;
    popover.querySelector("[data-minimum-popover-message]").textContent = details.message;
    popover.querySelector("[data-minimum-popover-note]").textContent = details.note;
  }

  root.ArchiveRewardsVoucherMinimum = {
    appliesTo,
    minimumEligibleOrderCents,
    formatMoney,
    describe,
    createPopoverState,
    place,
    render,
    HOVER_CLOSE_DELAY_MS
  };
})(typeof window !== "undefined" ? window : globalThis);
